import { isString, pick, isEmpty, flow } from 'lodash/fp';
import request from 'utils/request_ajax';
import REGEX from 'constants/regex';
import { routes as ROUTES, TYPES, PATH_MODES } from 'constants/config';
import * as actions from './actionCreators';
import { registerAssignableIdentity } from '../assign/helpers';
import {
  getPathRevisionData,
  validateWaitStep as createWaitStepValidator,
} from '../../selectors/pathBuilder';

export { toggleEditingUIElementByName } from './actionCreators';

function registerPathAsAssignableIdentity({ id, title }) {
  registerAssignableIdentity({ id, type: TYPES.PATH, title });
}

function setWindowPathName(mode) {
  let { pathname } = window.location;
  pathname = pathname.replace(REGEX.PATH_MODES_TRAILING_URL, '');
  pathname += '/' + mode;
  window.history.replaceState({}, null, pathname);
}

export function setMode(mode) {
  setWindowPathName(mode);
  return actions.setMode(mode);
}

/**
 * @param {...Function} reducers Reducers
 * @return {Function} Contents reducer
 */
function reducePathContents(...reducers) {
  return contents => flow(...reducers)(contents);
}

function removeInvalidWaitSteps(contents) {
  return contents.reduce((validContents, content, index) => {
    if (content.type === TYPES.WAITSTEP) {
      /**
       * Building a state object to use within the validateWaitStep selector and
       * explicitly setting state.mode to 'view' in order to validate/reject the
       * last wait step if exists. See selectors/pathBuilder.js#validateWaitStep
       */
      const state = { contents, mode: PATH_MODES.VIEW };
      const validateWaitStep = createWaitStepValidator();
      if (validateWaitStep(state, { ...content, index }).invalid) {
        return validContents;
      }
    }
    return validContents.concat(content);
  }, []);
}

function removeTemporaryIds(contents) {
  return contents.map(content => {
    if (isString(content.id) && content.id.match(/tmp/)) delete content.id;
    return content;
  });
}

export function updatePath(changes) {
  return dispatch =>
    new Promise(resolve => {
      dispatch(actions.updatePath(changes));
      resolve();
    });
}

export function toggleEditingMode({ updateUrl = true } = {}) {
  return (dispatch, getState) => {
    if (updateUrl) {
      // keep the url and state.mode === 'edit' in sync
      const { mode } = getState().pathBuilder;
      setWindowPathName(
        mode === PATH_MODES.EDIT ? PATH_MODES.VIEW : PATH_MODES.EDIT,
      );
    }
    return dispatch(actions.toggleEditingMode());
  };
}

export function saveRevision() {
  return (dispatch, getState) => {
    const state = getState().pathBuilder;
    const draftData = getPathRevisionData(state);
    dispatch(actions.saveRevision(draftData));
  };
}

export function publishPath() {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const {
        pathBuilder: { id },
      } = getState();
      dispatch(actions.updateRequestInProgress());
      request
        .put(ROUTES.PUBLISH_PATH(id))
        .then(response => {
          dispatch(actions.updateRequestSuccess());
          dispatch(actions.updatePath(response));
          registerPathAsAssignableIdentity({ id, title: response.title });
          resolve();
        })
        .fail(() => {
          dispatch(actions.updateRequestFailure());
          reject();
        });
    });
}

/**
 * @todo
 *
 * Dear future self:
 * please refactor this action. It is doing too much! It's creating a new path,
 * saving a draft, and updating a published path.
 * The name is also misleading. (╯°□°）╯︵ ┻━┻
 */
export function saveDraft(options = {}) {
  return (dispatch, getState) =>
    new Promise(resolve => {
      const state = getState().pathBuilder;
      const pathData = getPathRevisionData(state);
      const isNewPath = !state.id;

      /**
       * Reducing path contents in compliance with the backend requirements
       */
      Object.assign(pathData, {
        contents: reducePathContents(
          removeInvalidWaitSteps,
          removeTemporaryIds,
        )(pathData.contents),
      });

      if (isNewPath) {
        // Since the path draft only includes contents and title, there are some
        // keys that won't be returned from selectors.getPathRevisionData().
        // Therefore, if we're creating a new path we still need to add the
        // following keys to the request
        Object.assign(pathData, {
          description: state.description,
          enforcedOrder: state.enforcedOrder,
          workspace_id: state.workspace.id,
          certification: state.certification,
        });
      }

      /**
       * Optionally, we can pass a `merge` object under options to `saveDraft`.
       * This object will be used to merge any additional data to the original
       * request body sent to the backend.
       *
       * @example
       *  saveDraft({ merge: { assignNewContent: true } });
       *  // Request body will be { contents, title, assignNewContent: true }
       */
      if (!isEmpty(options.merge)) {
        Object.assign(pathData, options.merge);
      }

      pathData.title = pathData.title.trim();

      dispatch(actions.updateRequestInProgress());

      // handle two HTTP methods to create/update a path
      const { putJSON, postJSON } = request;
      const sendJSON = isNewPath ? postJSON : putJSON;

      const pathRoute = isNewPath
        ? ROUTES.PATHS
        : `${ROUTES.PATHS}/${state.id}`;
      sendJSON
        .call(request, pathRoute, pathData)
        .then(({ success, ...response }) => {
          if (success) {
            dispatch(actions.updatePath(response));
            dispatch(saveRevision());
            if (isNewPath) {
              window.history.replaceState(
                {},
                null,
                `/paths/${response.id}/view`,
              );
            }
            if (state.mode === PATH_MODES.EDIT) {
              dispatch(toggleEditingMode({ updateUrl: !isNewPath }));
            }
            if (
              !isEmpty(response.publishedAt) &&
              state.lastRevision.title !== response.title
            ) {
              const { id, title } = response;
              registerPathAsAssignableIdentity({ id, title });
            }
          }
          dispatch(actions.updateRequestSuccess());
          resolve(response);
        })
        .fail(() => {
          dispatch(actions.updateRequestFailure());
        });
    });
}

export function updatePathPublicity(isPublic) {
  return (dispatch, getState) => {
    const { id: pathId } = getState().pathBuilder;
    request.put(ROUTES.PATH(pathId), { public: isPublic }).then(response => {
      const attrs = pick(['public', 'urls'], response);
      dispatch(updatePath(attrs));
    });
  };
}

export function restoreRevision() {
  return dispatch => {
    dispatch(actions.restoreRevision());
    dispatch(toggleEditingMode());
  };
}

export function toggleEditingDescription() {
  return actions.toggleEditingDescription();
}

export function toggleEditingOrderEnforcement() {
  return actions.toggleEditingOrderEnforcement();
}

export const setEstimatedMinutesToComplete = value => {
  return dispatch => dispatch(actions.setEstimatedMinutesToComplete(value));
};
